import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useApi } from '@/hooks/useApi';
import { useEffectiveRole } from '@/stores/authStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Loader2, Star, ThumbsUp, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface CommunityWorkout {
  id: string;
  name: string;
  description: string | null;
  movements: string | null;
  scoring_type: string | null;
  created_at: string;
  author_name: string | null;
  vote_count: number;
  is_favorite: boolean | number;
}

type CommunityTab = 'global' | 'gym';

export default function Community() {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const effectiveRole = useEffectiveRole();
  const [tab, setTab] = useState<CommunityTab>('global');
  const [favoritePendingId, setFavoritePendingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['community-workouts', tab],
    queryFn: async () => {
      const endpoint =
        tab === 'gym' ? '/programming/community/gym?limit=30' : '/programming/community?limit=30';
      const res = await api.get<{ workouts: CommunityWorkout[]; affiliate_id?: string | null }>(
        endpoint
      );
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
  });

  const workouts = data?.workouts || [];
  const noGymContext =
    tab === 'gym' && data && (data as { affiliate_id?: string | null }).affiliate_id === null;

  const toggleFavorite = async (workout: CommunityWorkout) => {
    if (favoritePendingId) return;
    setFavoritePendingId(workout.id);
    try {
      const response = await api.post(`/programming/workouts/${workout.id}/favorite`, {
        is_favorite: !workout.is_favorite,
      });
      if (!response.success) throw new Error(response.error || 'Failed to update Favourite');
      await queryClient.invalidateQueries({ queryKey: ['community-workouts'] });
      await queryClient.invalidateQueries({ queryKey: ['personal-programming'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update Favourite');
    } finally {
      setFavoritePendingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Community</h1>
        <p className="text-muted-foreground">
          Workouts shared by the ErgShoot community. Create your own and tick "Share to global
          community" or "Share to my gym" to join in.
        </p>
      </div>

      {/* FEAT-APR04-004 BE-APR04-004c — gym vs global tabs */}
      <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-card p-1">
        <Button
          type="button"
          variant={tab === 'global' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setTab('global')}
          className="gap-2"
        >
          <Globe className="h-4 w-4" />
          Global
        </Button>
        <Button
          type="button"
          variant={tab === 'gym' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setTab('gym')}
          className="gap-2"
        >
          <Users className="h-4 w-4" />
          My Gym
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">Failed to load community feed</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && workouts.length === 0 && noGymContext && (
        <Card>
          <CardHeader>
            <CardTitle>My Gym Feed</CardTitle>
            <CardDescription>Workouts shared by your gym community</CardDescription>
          </CardHeader>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">You're not part of a gym yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Join a gym to see workouts shared by your trainer and gym community. Browse the Global
              tab to see what the wider ErgShoot community is doing.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && workouts.length === 0 && !noGymContext && (
        <Card>
          <CardHeader>
            <CardTitle>{tab === 'gym' ? 'My Gym Feed' : 'Community Feed'}</CardTitle>
            <CardDescription>
              {tab === 'gym'
                ? 'Workouts shared by your gym community'
                : 'See what other athletes are up to'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No shared workouts yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              {tab === 'gym'
                ? "Your trainer and gym community haven't shared anything here yet."
                : 'Be the first to share a workout to the global community.'}
            </p>
          </CardContent>
        </Card>
      )}

      {workouts.length > 0 && (
        <div className="grid gap-4">
          {workouts.map((w) => (
            <Card key={w.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">{w.name}</CardTitle>
                    <WorkoutCreatorMetadata
                      created_at={w.created_at}
                      created_by_username={w.author_name}
                      fallbackLabel="an athlete"
                    />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3" />
                      {w.vote_count}
                    </Badge>
                    {effectiveRole !== 'hq_admin' && (
                      <Button
                        type="button"
                        size="sm"
                        variant={w.is_favorite ? 'default' : 'outline'}
                        aria-label={w.is_favorite ? 'Remove from Favourites' : 'Add to Favourites'}
                        aria-pressed={Boolean(w.is_favorite)}
                        disabled={favoritePendingId !== null}
                        onClick={(event) => {
                          event.stopPropagation();
                          void toggleFavorite(w);
                        }}
                      >
                        <Star className={`h-4 w-4 ${w.is_favorite ? 'fill-current' : ''}`} />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {w.movements && (
                  <pre className="whitespace-pre-wrap font-mono text-sm text-foreground bg-muted/50 border border-border rounded-md p-3">
                    {w.movements}
                  </pre>
                )}
                {!w.movements && w.description && (
                  <p className="text-sm text-muted-foreground">{w.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
